import type { Ref } from 'react';
import { ApnsCredentialFields, type ApnsCredentialFieldErrors } from '@/features/push-credentials';

export type ApnsFields = {
  keyId: string;
  teamId: string;
  bundleId: string;
  file: File | null;
};

export const EMPTY_APNS_FIELDS: ApnsFields = { keyId: '', teamId: '', bundleId: '', file: null };

export type ApnsFieldsViewProps = {
  fields: ApnsFields;
  errors: ApnsCredentialFieldErrors;
  ref?: Ref<HTMLDivElement>;
  sandbox: boolean;
  savedFileName: string | null;
  isEditing: boolean;
  canEdit: boolean;
  isBusy: boolean;
  isInvalid: boolean;
  onChange: (patch: Partial<ApnsFields>) => void;
  onSandboxChange: (checked: boolean) => void;
};

/**
 * The sandbox switch stays usable outside editing: on a stored credential it opens
 * a prompt, which is how the endpoint changes without re-uploading the key.
 */
export function ApnsFieldsView({
  fields,
  errors,
  ref,
  sandbox,
  savedFileName,
  isEditing,
  canEdit,
  isBusy,
  isInvalid,
  onChange,
  onSandboxChange,
}: ApnsFieldsViewProps) {
  const isLocked = !canEdit || isBusy;

  return (
    <ApnsCredentialFields
      ref={ref}
      variant="settings"
      bundleId={fields.bundleId}
      keyId={fields.keyId}
      teamId={fields.teamId}
      sandbox={sandbox}
      selectedFileName={fields.file?.name ?? null}
      savedFileName={savedFileName}
      isEditable={isEditing}
      isInvalid={isInvalid}
      errors={errors}
      isDisabled={isLocked || !isEditing}
      isSandboxDisabled={isLocked}
      onBundleIdChange={(bundleId) => onChange({ bundleId })}
      onKeyIdChange={(keyId) => onChange({ keyId })}
      onTeamIdChange={(teamId) => onChange({ teamId })}
      onSandboxChange={onSandboxChange}
      onFileSelected={(file) => onChange({ file })}
    />
  );
}
